import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { authApi } from '@/utils/api'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'company_admin' || user.value?.role === 'super_admin')
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin')
  const companyId = computed(() => user.value?.company_id)

  // Actions
  const setToken = (newToken: string | null) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  const setUser = (newUser: User | null) => {
    user.value = newUser
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser))
    } else {
      localStorage.removeItem('user')
    }
  }

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    loading.value = true
    try {
      const response = await authApi.login(email, password, recaptchaToken)
      console.log('Login response:', response.data)
      const { access_token, user: userData } = response.data
      
      setToken(access_token)
      setUser(userData)
      
      return { success: true }
    } catch (error: any) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao fazer login'
      return {
        success: false,
        error: errorMessage
      }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
  }

  const fetchUser = async () => {
    if (!token.value) return
    
    try {
      const response = await authApi.me()
      setUser(response.data)
    } catch (error) {
      // Se falhar, limpa o token
      logout()
    }
  }

  const init = () => {
    // Tenta carregar usuário do localStorage
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        localStorage.removeItem('user')
      }
    }
    
    // Se tem token mas não tem usuário, busca o usuário
    if (token.value && !user.value) {
      fetchUser()
    }
  }

  return {
    // State
    user,
    token,
    loading,
    // Getters
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    companyId,
    // Actions
    login,
    logout,
    fetchUser,
    init,
    setToken,
    setUser
  }
})
